import { GeneralCustomsDeclaration } from '~api/som';

import { ReactNode } from 'react';

import { ListLayout, Modal, ModalLayout } from '@ingrid/luterkort-react';

import { AddressMultiline } from '~src/components/AddressMultiline/AddressMultiline';
import { Currency } from '~src/components/Currency/Currency';
import { PlainTable } from '~src/components/PlainTable/PlainTable';
import { Weight } from '~src/components/Weight/Weight';
import { LocalizedDate } from '~src/i18n/localized-date';
import { useTranslation } from '~src/i18n/use-translation';

interface Props {
  declaration: GeneralCustomsDeclaration;
  trigger: ReactNode;
}

export function CustomsDeclarationPreview(props: Props) {
  const { trigger, declaration } = props;
  const { t } = useTranslation();

  return (
    <Modal.Root>
      <Modal.Trigger>{trigger}</Modal.Trigger>

      <Modal.Content style={{ width: '100%', maxWidth: '1200px' }}>
        <Modal.Header title={t('common.customsDeclarations')} />

        <Modal.Body>
          <ListLayout.Row
            style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', paddingBottom: '8px' }}
          >
            <ListLayout.Column style={{ gap: '4px' }}>
              <ListLayout.Text variant="secondary">{t('shipments.customsDeclarations.payersName')}</ListLayout.Text>
              <ListLayout.Text>{declaration.sold_to?.name ?? '-'}</ListLayout.Text>
            </ListLayout.Column>

            <ListLayout.Column style={{ gap: '4px' }}>
              <ListLayout.Text variant="secondary">{t('shipments.customsDeclarations.payersAddress')}</ListLayout.Text>
              <ListLayout.Text>
                {declaration.sold_to ? <AddressMultiline address={{ ...declaration.sold_to, name: undefined }} /> : '-'}
              </ListLayout.Text>
            </ListLayout.Column>

            <ListLayout.Column style={{ gap: '4px' }}>
              <ListLayout.Text variant="secondary">{t('shipments.customsDeclarations.contentsType')}</ListLayout.Text>
              <ListLayout.Text>
                {declaration.contents_type
                  ? t(`shipments.customsDeclarations.contentsType.${declaration.contents_type}`)
                  : '-'}
              </ListLayout.Text>
            </ListLayout.Column>

            <ListLayout.Column style={{ gap: '4px' }}>
              <ListLayout.Text variant="secondary">{t('common.incoterms')}</ListLayout.Text>
              <ListLayout.Text>
                {declaration.incoterms ? t(`shipments.customsDeclarations.incoterms.${declaration.incoterms}`) : '-'}
              </ListLayout.Text>
            </ListLayout.Column>

            <ListLayout.Column style={{ gap: '4px' }}>
              <ListLayout.Text variant="secondary">{t('common.totalGrossWeight')}</ListLayout.Text>
              <ListLayout.Text>{declaration.total_gross_weight ?? '-'}</ListLayout.Text>
            </ListLayout.Column>

            <ListLayout.Column style={{ gap: '4px' }}>
              <ListLayout.Text variant="secondary">{t('common.currency')}</ListLayout.Text>
              <ListLayout.Text>{declaration.currency ?? '-'}</ListLayout.Text>
            </ListLayout.Column>

            <ListLayout.Column style={{ gap: '4px' }}>
              <ListLayout.Text variant="secondary">{t('common.invoiceDate')}</ListLayout.Text>
              <ListLayout.Text>
                {declaration.invoice_date ? <LocalizedDate dateTime={declaration.invoice_date} /> : '-'}
              </ListLayout.Text>
            </ListLayout.Column>

            <ListLayout.Column style={{ gap: '4px' }}>
              <ListLayout.Text variant="secondary">{t('common.invoiceNumber')}</ListLayout.Text>
              <ListLayout.Text>{declaration.invoice_number ?? '-'}</ListLayout.Text>
            </ListLayout.Column>

            <ListLayout.Column style={{ gap: '4px' }}>
              <ListLayout.Text variant="secondary">{t('common.sellerTaxIdNumber')}</ListLayout.Text>
              <ListLayout.Text>{declaration.seller_tax_id_number ?? '-'}</ListLayout.Text>
            </ListLayout.Column>

            <ListLayout.Column style={{ gap: '4px' }}>
              <ListLayout.Text variant="secondary">{t('common.buyerTaxIdNumber')}</ListLayout.Text>
              <ListLayout.Text>{declaration.buyer_tax_id_number ?? '-'}</ListLayout.Text>
            </ListLayout.Column>

            <ListLayout.Column style={{ gap: '4px' }}>
              <ListLayout.Text variant="secondary">
                {t('shipments.customsDeclarations.contentsExplanation')}
              </ListLayout.Text>
              <ListLayout.Text>{declaration.contents_explanation ?? '-'}</ListLayout.Text>
            </ListLayout.Column>
          </ListLayout.Row>

          <ModalLayout.Title>{t('common.itemsDetails')}</ModalLayout.Title>
          <PlainTable>
            <PlainTable.Head>
              <PlainTable.Row style={{ borderBottom: 0 }}>
                <PlainTable.Cell as="th" style={{ width: '25px' }}>
                  -
                </PlainTable.Cell>
                <PlainTable.Cell as="th">{t('common.name')}</PlainTable.Cell>
                <PlainTable.Cell as="th">SKU</PlainTable.Cell>
                <PlainTable.Cell as="th">{t('common.brand')}</PlainTable.Cell>
                <PlainTable.Cell as="th">{t('common.hsTariffNumber')}</PlainTable.Cell>
                <PlainTable.Cell as="th">{t('common.value')}</PlainTable.Cell>
                <PlainTable.Cell as="th">{t('shipments.customsDeclarations.netGrossWeightGrams')}</PlainTable.Cell>
                <PlainTable.Cell as="th">{t('common.origin')}</PlainTable.Cell>
              </PlainTable.Row>
            </PlainTable.Head>

            <PlainTable.Body>
              {declaration.items?.map((item, index) => (
                <PlainTable.Row key={`customs-declaration-item-${index}`}>
                  <PlainTable.Cell>{item.quantity}&times;</PlainTable.Cell>
                  <PlainTable.Cell>{item.name}</PlainTable.Cell>
                  <PlainTable.Cell>{item.sku}</PlainTable.Cell>
                  <PlainTable.Cell>{item.brand}</PlainTable.Cell>
                  <PlainTable.Cell>{item.hs_tariff_number}</PlainTable.Cell>
                  <PlainTable.Cell>
                    {item.unit_value && declaration.currency ? (
                      <Currency value={item.unit_value} currency={declaration.currency} />
                    ) : (
                      '-'
                    )}
                  </PlainTable.Cell>
                  <PlainTable.Cell>
                    <Weight weight={item.unit_net_weight} />/<Weight weight={item.unit_gross_weight} />
                  </PlainTable.Cell>
                  <PlainTable.Cell>{item.country_of_origin}</PlainTable.Cell>
                </PlainTable.Row>
              ))}
            </PlainTable.Body>
          </PlainTable>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}
